import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import WorkRow from "@/components/base/WorkRow";
import type { WorkType } from "@/types/work";
import {
  countPages,
  filterWorks,
  paginate,
  sortWorks,
  type SortKey,
} from "@/lib/workUtils";
import FilterPanel from "./components/FilterPanel";
import SortBar from "./components/SortBar";

const typeMeta: Record<WorkType, { title: string; desc: string }> = {
  movie: {
    title: "电影分类",
    desc: "汇集悬疑、犯罪、科幻、奇幻等类型影片，提供完整剧情介绍与演员资料，支持按类型、地区、年份多维筛选。",
  },
  tv: {
    title: "电视剧分类",
    desc: "覆盖都市、古装、罪案、科幻等剧集，实时追踪更新进度与演职员信息，帮助你快速找到正在追的剧。",
  },
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const workType: WorkType = type === "tv" ? "tv" : "movie";

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [sort, setSort] = useState<SortKey>("hot");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setSort("hot");
    setPage(1);
  }, [workType]);

  const results = useMemo(() => {
    const filtered = filterWorks({
      type: workType,
      genres: selectedGenres,
      regions: selectedRegions,
      years: selectedYears,
    });
    return sortWorks(filtered, sort);
  }, [workType, selectedGenres, selectedRegions, selectedYears, sort]);

  const totalPages = countPages(results.length);
  const pageItems = paginate(results, page);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 320);
    return () => clearTimeout(timer);
  }, [workType, selectedGenres, selectedRegions, selectedYears, sort, page]);

  const toggle = <T,>(list: T[], value: T, setter: (next: T[]) => void) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRegions([]);
    setSelectedYears([]);
    setPage(1);
  };

  const meta = typeMeta[workType];

  useDocumentMeta({
    title: `${meta.title}-妻子的姐姐`,
    description: meta.desc,
    keywords: `妻子的姐姐,${meta.title},${workType === "tv" ? "电视剧" : "电影"},剧情介绍,演员信息,影视更新`,
  });

  return (
    <PageShell>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: workType === "tv" ? "电视剧" : "电影" },
            ]}
          />
          <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
            {meta.title}
            <span className="ml-3 align-middle text-sm font-medium text-foreground-400">
              妻子的姐姐
            </span>
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            {meta.desc}
          </p>
          <div className="mt-5 flex items-center gap-2">
            {(["movie", "tv"] as WorkType[]).map((t) => (
              <Link
                key={t}
                to={`/category/${t}`}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  t === workType
                    ? "bg-primary-700 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:text-primary-700"
                }`}
              >
                {t === "movie" ? "电影" : "电视剧"}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full flex-col gap-6 px-4 py-8 md:px-8 md:py-10 lg:flex-row lg:gap-8">
        <FilterPanel
          selectedGenres={selectedGenres}
          selectedRegions={selectedRegions}
          selectedYears={selectedYears}
          open={filterOpen}
          onToggleOpen={() => setFilterOpen((v) => !v)}
          onGenreChange={(v) => toggle(selectedGenres, v, setSelectedGenres)}
          onRegionChange={(v) => toggle(selectedRegions, v, setSelectedRegions)}
          onYearChange={(v) => toggle(selectedYears, v, setSelectedYears)}
          onReset={resetFilters}
        />

        <div className="min-w-0 flex-1">
          <SortBar sort={sort} total={results.length} onChange={(s) => { setSort(s); setPage(1); }} />

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="flex animate-pulse gap-4 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4"
                >
                  <div className="h-40 w-28 shrink-0 rounded-md bg-background-200 md:h-48 md:w-32" />
                  <div className="flex-1 space-y-3 py-2">
                    <div className="h-4 w-1/3 rounded bg-background-200" />
                    <div className="h-3 w-2/3 rounded bg-background-200" />
                    <div className="h-3 w-1/2 rounded bg-background-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <EmptyState
              title="没有找到匹配的影视作品"
              description="当前筛选条件下暂无结果，试试减少筛选条件或直接重置，查看该分类下的全部内容。"
              icon="ri-film-line"
              actionLabel="重置筛选条件"
              onAction={resetFilters}
            />
          ) : (
            <>
              <div className="space-y-3">
                {pageItems.map((work) => (
                  <WorkRow key={work.id} work={work} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
}